import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import GenericModal from "../../../../../Components/Modal/GenericModal";
import { Button } from "../../../../../ui/button";
import { Input } from "../../../../../ui/input";
import { Label } from "../../../../../ui/label";
import { Textarea } from "../../../../../ui/textarea";
import { Card, CardContent } from "../../../../../ui/card";
import {
  enableGlobalBenefit,
} from "../../../../../Services/adminBenefits";
import {
  globalEnablementOverridesSchema,
  toEnablementOverridesPayload,
  enablementToFormValues,
} from "../../schemas";
import { humanEnum, extractApiError } from "./utils";

const EMPTY_OVERRIDES = {
  override_portal_url: "",
  override_contact_phone: "",
  override_contact_email: "",
  override_suggested_intro: "",
  override_priority: "",
};

const ReadOnlyRow = ({ label, value }) => (
  <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span>{value || <span className="text-muted-foreground">—</span>}</span>
  </div>
);

/**
 * Dialog to enable a global benefit for this corporate, optionally with
 * overrides. Re-opens for existing enablements (edit overrides).
 *
 * Per BENEFITS_API.md §2.4: only five fields are overridable. The underlying
 * global's identity is intentionally read-only.
 */
const GlobalOverrideDialog = ({
  open,
  onOpenChange,
  globalBenefit,
  enablement,
  onSaved,
}) => {
  const { id: corporateId } = useParams();
  const [submitError, setSubmitError] = useState(null);

  const isEnable = !enablement?.is_enabled;

  const defaultValues = useMemo(
    () => (enablement ? enablementToFormValues(enablement) : EMPTY_OVERRIDES),
    [enablement]
  );

  const form = useForm({
    resolver: zodResolver(globalEnablementOverridesSchema),
    defaultValues,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (open) {
      reset(defaultValues);
      setSubmitError(null);
    }
  }, [open, defaultValues, reset]);

  const onSubmit = async (values) => {
    setSubmitError(null);
    if (!globalBenefit?.id) {
      setSubmitError("Missing global benefit identifier.");
      return;
    }
    try {
      const payload = toEnablementOverridesPayload(values);
      const result = await enableGlobalBenefit(
        corporateId,
        globalBenefit.id,
        payload
      );
      onSaved?.(result);
      onOpenChange?.(false);
    } catch (err) {
      setSubmitError(extractApiError(err, "Failed to save overrides."));
    }
  };

  if (!globalBenefit) return null;

  return (
    <GenericModal
      isOpen={open}
      onClose={() => onOpenChange?.(false)}
      heading={isEnable ? "Enable global benefit" : "Configure overrides"}
      primaryLabel=""
      secondaryLabel=""
      maxWidth="720px"
    >
      <div className="w-full text-left">
        <p className="text-sm text-muted-foreground">
          Only the five fields below are overridable. The underlying global
          benefit's identity (name, category, signals, follow-ups, delivery
          methods, copy) is locked by design.
        </p>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="grid gap-4 mt-4">
        <div className="max-h-[55vh] overflow-y-auto px-1 pt-2 pb-1 grid gap-4">

        <Card className="bg-muted/20">
          <CardContent className="pt-4">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Underlying global
            </h4>
            <ReadOnlyRow label="Name" value={globalBenefit.name} />
            <ReadOnlyRow label="Provider" value={globalBenefit.provider} />
            <ReadOnlyRow
              label="Category"
              value={humanEnum(globalBenefit.category)}
            />
            <ReadOnlyRow
              label="Delivery"
              value={(globalBenefit.delivery_methods || [])
                .map(humanEnum)
                .join(", ")}
            />
            <ReadOnlyRow
              label="Follow-ups"
              value={(globalBenefit.follow_up_actions || [])
                .map(humanEnum)
                .join(", ")}
            />
            <ReadOnlyRow
              label="Signals"
              value={(globalBenefit.signals || [])
                .map((s) => s.name)
                .join(", ")}
            />
            {globalBenefit.helps_with && (
              <ReadOnlyRow label="Helps with" value={globalBenefit.helps_with} />
            )}
          </CardContent>
        </Card>

          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Your overrides
          </h4>
          <p className="text-xs text-muted-foreground -mt-2">
            Leave any field blank to fall back to the global's default value.
          </p>

          <div className="grid gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="ovr_portal">Portal URL</Label>
              <Input
                id="ovr_portal"
                type="url"
                placeholder={globalBenefit.portal_url || "https://…"}
                {...register("override_portal_url")}
              />
              {errors.override_portal_url?.message && (
                <p className="text-xs text-destructive">
                  {errors.override_portal_url.message}
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ovr_phone">Contact phone</Label>
              <Input
                id="ovr_phone"
                placeholder={globalBenefit.contact_phone || "+1-555-0100"}
                {...register("override_contact_phone")}
              />
              {errors.override_contact_phone?.message && (
                <p className="text-xs text-destructive">
                  {errors.override_contact_phone.message}
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ovr_email">Contact email</Label>
              <Input
                id="ovr_email"
                type="email"
                placeholder={globalBenefit.contact_email || "care@example.com"}
                {...register("override_contact_email")}
              />
              {errors.override_contact_email?.message && (
                <p className="text-xs text-destructive">
                  {errors.override_contact_email.message}
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ovr_intro">Suggested intro</Label>
              <Textarea
                id="ovr_intro"
                rows={3}
                maxLength={2000}
                placeholder={
                  globalBenefit.suggested_intro ||
                  "Your organization provides…"
                }
                {...register("override_suggested_intro")}
              />
              {errors.override_suggested_intro?.message && (
                <p className="text-xs text-destructive">
                  {errors.override_suggested_intro.message}
                </p>
              )}
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="ovr_priority">Priority</Label>
              <Input
                id="ovr_priority"
                type="number"
                min={0}
                placeholder={String(globalBenefit.priority ?? 100)}
                {...register("override_priority")}
              />
              <p className="text-xs text-muted-foreground">
                Tiebreaker only — lower wins on ties between matching benefits.
              </p>
              {errors.override_priority?.message && (
                <p className="text-xs text-destructive">
                  {errors.override_priority.message}
                </p>
              )}
            </div>
          </div>
          </div>

          {submitError && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving…"
                : isEnable
                ? "Enable for this corporate"
                : "Save overrides"}
            </Button>
          </div>
        </form>
      </div>
    </GenericModal>
  );
};

export default GlobalOverrideDialog;
